<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { EasePack } from 'gsap/EasePack'
import type { SectionLogosListRecord } from '~/types/datocms'
import { useAppStore } from '~/stores/AppStore'

const props = defineProps<{
  data: SectionLogosListRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const store = useAppStore()
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  nextTick(() => {
    if (ctxRef.value) {
      gsap.registerPlugin(ScrollTrigger, EasePack)

      ctx = gsap.context(() => {
        const items = gsap.utils.toArray('.fx-sl-logo')
        if (!items.length) return

        const tl = useHorizontalLoop(items, {
          paused: true,
          speed: 0.5,
          repeat: -1
        })

        ScrollTrigger.create({
          trigger: ctxRef.value,
          start: 'top bottom',
          markers: false,
          onEnter: () => {
            tl?.play()
          },
          onLeave: () => {
            tl?.pause()
          },
          onEnterBack: () => {
            tl?.play()
          },
          onLeaveBack: () => {
            tl?.pause()
          }
        })
        ScrollTrigger.refresh()
      }, ctxRef.value as HTMLElement)
    }
  })
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div ref="ctxRef" class="w-full overflow-hidden py-12 lg:py-24" :class="invertedStyles">
    <Heading
      v-if="data.title"
      class="mx-auto mb-4 max-w-[678px] text-center uppercase lg:mb-8"
      :level="level"
      :size="4"
      :is-semantic="isSemantic"
    >
      <TextReveal>{{ data.title }}</TextReveal>
    </Heading>
    <div
      v-if="data.records.length"
      class="relative mx-auto flex w-full items-center justify-center overflow-hidden"
    >
      <div
        v-for="(logo, idx) in data.records"
        :key="idx"
        class="fx-sl-logo flex-shrink-0 transform-gpu px-10 will-change-transform"
      >
        <Image
          v-if="logo.logo"
          loading="lazy"
          class="max-w-15 mix-blend-difference lg:max-w-18"
          :class="{
            invert: store.theme === 'Dark' || (store.theme === 'Light' && data.invertColorTheme)
          }"
          :src="logo.logo?.responsiveImage ? logo.logo?.responsiveImage?.src : logo.logo?.url"
          :srcset="logo.logo?.responsiveImage ? logo.logo?.responsiveImage?.webpSrcSet : undefined"
          :width="
            logo.logo?.responsiveImage
              ? logo.logo?.responsiveImage?.width
              : (logo.logo?.width ?? 80)
          "
          :height="
            logo.logo?.responsiveImage
              ? logo.logo?.responsiveImage?.height
              : (logo.logo?.height ?? 80)
          "
          :alt="logo.logo?.alt ?? 'Logo'"
        />
      </div>
      <div
        class="pointer-events-none absolute left-1/2 top-0 h-full w-full max-w-[100vw] -translate-x-1/2"
      ></div>
    </div>
  </div>
</template>
